import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellCenterV8,
  GridLinkButton,
} from 'motion-components';
import { generatePath, Link, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  dateColumn,
  datetimeColumn,
} from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import { Company } from 'shared-pages/common/constants';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Driver'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      // EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'firstDate',
        header: t('Ημ/νία Λήψης'),
        columnName: t('Ημ/νία Λήψης'),
        accessorKey: 'firstDate',
        orderByField: 'firstDate',
        ...datetimeColumn(t),
      },
      {
        id: 'commandType.name',
        header: t('Ενέργεια'),
        columnName: t('Ενέργεια'),
        accessorKey: 'commandType.name',
        orderByField: 'commandType.name',
        cell: TableCellV8,
        size: 20,
        meta: { type: 'text' },
      },
      ...(appStore.isPlatformOwner
        ? [
            {
              id: 'collectorId',
              header: t('Συλλέκτης'),
              columnName: t('Συλλέκτης'),
              accessorKey: 'driver.collector.name',
              orderByField: 'driver.collector.name',
              cell: ({ row }) => {
                // const collectorName = row.original.driver?.collector?.name;
                const collectorName = row.original.driver?.collector?.name;
                return <div>{collectorName ? collectorName : '-'}</div>;
              },
              size: 20,
              meta: { type: 'text' },
            },
          ]
        : []),

      {
        id: 'driver.name',
        header: t('Ονοματεπώνυμο Οδηγού'),
        columnName: t('Ονοματεπώνυμο Οδηγού'),
        accessorKey: 'driver.name',
        orderByField: 'driver.name',
        cell: ({ row }) => {
          const driverName = row.original.driver?.name;
          const collectorName = row.original.driver?.collector?.name;
          return (
            <div>
              {driverName ? driverName : '-'}
              <div className="text-sm"> {collectorName ? collectorName : '-'}</div>
            </div>
          );
        },
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'vehicle.plateNumber',
        header: t('Πινακίδα Οχήματος'),
        columnName: t('Πινακίδα Οχήματος'),
        accessorKey: 'vehicle.plateNumber',
        orderByField: 'vehicle.plateNumber',
        cell: TableCellV8,
        size: 10,
        meta: { type: 'text' },
      },

      {
        id: 'context',
        header: t('Στοιχεία Ενέργειας'),
        columnName: t('Στοιχεία Ενέργειας'),
        accessorKey: 'context',
        orderByField: 'context',
        cell: ({ row }) => {
          const value = row.original.context;
          let valueObj: Record<string, unknown> = {};
          try {
            valueObj = JSON.parse(value || '{}');
          } catch {
            return <div>{value}</div>;
          }

          const labelMap: Record<string, string> = {
            collectionOrderId: t('Αίτημα'),
            collectionPointId: t('Σημείο συλλογής'),
            collectionpointId: t('Σημείο συλλογής'),
            routeLineId: t('Γραμμή δρομολογίου'),
            notes: t('Σημειώσεις'),
            finDispNote: t('Αρ. Δελτίου Αποστολής'),
            dateCollected: t('Ημ/νία συλλογής'),
          };

          return (
            <div>
              {Object.entries(valueObj).map(([key, val]) => {
                const label = labelMap[key] ?? key;
                if (key === 'collectionpointId' || key === 'collectionPointId') {
                  const id = parseInt(String(val), 10);
                  const path = generatePath(getRouteByName('Collectionpoint'), { id });
                  return (
                    <div className="pb-2" key={key}>
                      <Link to={path}>
                        {label} = {id}
                      </Link>
                    </div>
                  );
                }
                if (key === 'collectionOrderId') {
                  const id = parseInt(String(val), 10);
                  const path = generatePath(getRouteByName('Order'), { id });
                  return (
                    <div className="pb-2" key={key}>
                      <Link to={path}>
                        {label} = {id}
                      </Link>
                    </div>
                  );
                }
                return (
                  <div className="pb-2" key={key}>
                    {label} = {typeof val === 'string' ? val : JSON.stringify(val)}
                  </div>
                );
              })}
            </div>
          );
        },
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'generatedId',
        header: t('Δημιουργηθήσα Εγγραφή'),
        columnName: t('Δημιουργηθήσα Εγγραφή'),
        accessorKey: 'generatedId',
        orderByField: 'generatedId',
        cell: ({ row }) => {
          const generatedId = row.original.generatedId;
          const generatedTable = row.original.generatedTable;
          if (generatedTable === 'idocs') {
            const path = generatePath(getRouteByName('Idoc'), { id: generatedId ?? 0 });
            return (
              <div>
                <Link to={path}>
                  {t('Έντυπο Αναγνώρισης με id')}: {generatedId}
                </Link>
              </div>
            );
          } else if (generatedTable === 'collections') {
            const path = generatePath(getRouteByName('Collection'), { id: generatedId ?? 0 });
            return (
              <div>
                <Link to={path}>
                  {t('Συλλογή με id')}: {generatedId}
                </Link>
              </div>
            );
          } else if (generatedTable === 'containersorders') {
            const path = generatePath(getRouteByName('ContainerOrder'), { id: generatedId ?? 0 });
            return (
              <div>
                <Link to={path}>
                  {t('Παραγγελία Συσκευασιών με id')}: {generatedId}
                </Link>
              </div>
            );
          } else if (generatedTable === 'collection_orders') {
            // Δήλωση συλλογής / προσθήκη σημείωσης — σύνδεση με το αίτημα
            const path = generatePath(getRouteByName('Order'), { id: generatedId ?? 0 });
            return (
              <div>
                <Link to={path}>
                  {t('Αίτημα με id')}: {generatedId}
                </Link>
              </div>
            );
          } else if (generatedTable === 'containers_qrcodes') {
            const path = generatePath(getRouteByName('QrCode'), { id: generatedId ?? 0 });
            return (
              <div>
                <Link to={path}>
                  {t('QR Code Συσκευασιών με id')}: {generatedId}
                </Link>
              </div>
            );
          } else
            return (
              <div>
                {generatedTable}-{generatedId}
              </div>
            );
        },
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'deviceInfo',
        header: t('Συσκευή / Θέση'),
        columnName: t('Συσκευή / Θέση'),
        accessorKey: 'deviceInfo',
        orderByField: 'deviceInfo',
        cell: ({ row }) => {
          const deviceInfo = () => {
            try {
              const obj = JSON.parse(row.original.deviceInfo);
              if (obj?.model || obj?.manufacturer)
                return `${obj?.manufacturer} ${obj?.model ?? '?'} - ${
                  obj?.osVersion ?? '?'
                } - App version: ${obj?.appVersion ?? '?'}`;
              else return `-`;
            } catch (error) {
              return '-';
            }
          };
          const latLong = () => {
            if (!row.original.latitude) return '-';
            const label = `${row.original.longitude}, ${row.original.latitude}`;
            return (
              <GridLinkButton
                onClick={() => {
                  window.open(
                    `https://www.google.com/maps/search/?api=1&query=${row.original.latitude}%2C${row.original.longitude}`,
                    '_new',
                  );
                }}
              >
                {label}
              </GridLinkButton>
            );
          };
          return (
            <div>
              {deviceInfo()}
              <br />
              {latLong()}
            </div>
          );
        },
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'processResultFlag',
        header: t('Κατάσταση Επεξεργασίας'),
        columnName: t('Κατάσταση Επεξεργασίας'),
        accessorKey: 'processResultFlag',
        orderByField: 'processResultFlag',
        cell: ({ row }) => {
          const flag = row.original.processResultFlag;
          const msg = row.original.processResultMsg;
          return (
            <div>
              {flag === true ? (
                <span className="text-success">OK</span>
              ) : (
                <span className="text-danger">ERROR: {msg}</span>
              )}
            </div>
          );
        },
        size: 20,
        meta: { type: 'text' },
      },
      // firstDateUserColumn(t),
      // lastDateUserColumn(t),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          style={{ overflow: 'visible' }}
          ref={tableRef}
          loading={loading}
          id="drivers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
